import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { ValidationError } from "../../errors";
import { BaseOptionItem, defineModel } from "../types";

const Z_IMAGE_ASPECT_RATIOS = [
  "1:1",
  "21:9",
  "16:9",
  "4:3",
  "3:2",
  "2:3",
  "3:4",
  "9:16",
  "9:21",
] as const;

const Z_IMAGE_ASPECT_RATIO_OPTIONS: readonly BaseOptionItem[] =
  Z_IMAGE_ASPECT_RATIOS.map((value) => ({
    label: value,
    value,
  }));

const Z_IMAGE_ASPECT_RATIO_DIMENSIONS = {
  "1:1": { width: 1024, height: 1024 },
  "21:9": { width: 1536, height: 640 },
  "16:9": { width: 1344, height: 768 },
  "4:3": { width: 1152, height: 896 },
  "3:2": { width: 1280, height: 832 },
  "2:3": { width: 832, height: 1280 },
  "3:4": { width: 896, height: 1152 },
  "9:16": { width: 768, height: 1344 },
  "9:21": { width: 640, height: 1536 },
} as const;

type ZImageAspectRatio = keyof typeof Z_IMAGE_ASPECT_RATIO_DIMENSIONS;

function getZImageDimension(aspectRatio: ZImageAspectRatio) {
  return Z_IMAGE_ASPECT_RATIO_DIMENSIONS[aspectRatio];
}

const zImageParams = z
  .object({
    aspectRatio: z.enum(Z_IMAGE_ASPECT_RATIOS).default("9:16"),
    prompt: z.string().default(""),
  })
  .strip();

export const zImage = defineModel({
  key: "z-image",
  stepType: "image",
  spec: {
    label: "Z-Image",
    description: "Cheap, fast and high quality image generation",
    icon: "qwen-logo.svg",
    tags: ["image-gen"],
    featured: true,
    features: { aspectRatio: true, prompts: true, variations: true } as const,
    options: {
      attachments: [],
      aspectRatio: { items: Z_IMAGE_ASPECT_RATIO_OPTIONS },
    },
    params: zImageParams,
  },
  referencePricing: {
    provider: "fal.ai",
    url: "https://fal.ai/models/fal-ai/qwen",
    entries: [{ unit: "per_image", unitPriceMicrodollars: 20_000 }],
  },
  providers: [
    {
      provider: "runware",
      modelId: "runware:z-image@turbo",
      estimateCost(_params) {
        // Using a reasonable default cost estimate
        return 0.0019 * 1_000_000 * (1 + BRAND_MARKUP);
      },
      toPayload(params) {
        if (!params?.prompt?.length) {
          throw new ValidationError(
            "Please enter a prompt to generate an image.",
          );
        }

        const size = getZImageDimension(params.aspectRatio);
        return {
          model: this.modelId,
          positivePrompt: params.prompt,
          width: size.width,
          height: size.height,
        };
      },
    },
  ],
});
